import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { xAxisHighLight } from '@intelligence/v3/utils/echarts';
import { getSortedDates as getSortedDatesUtils } from '@intelligence/utils/commonUtils';
import { SortType } from '@home/enums/common';

/**
 * 获取排序后的dates，默认升序
 * @param dates 日期列表
 * @return dates 排序后的日期列表
 */
const getSortedDatesDefault = (dates: string[]): string[] => {
  return getSortedDatesUtils(dates, SortType.Asc);
};

/**
 * 生成echarts折线图x轴dates、series
 * @param list echarts折线图数据
 * @param configs 其他配置 具体配置参见ILineChartGetterHelpers定义
 * @return echarts折线图x轴dates、series
 */
/**
 * 检查值是否有效（不为空）
 * @param value 值
 * @returns 是否有效
 */
const isValidValue = (value: number): boolean => {
  return typeof value === 'number' && !Number.isNaN(value);
};

/**
 * 获取指定系列在指定日期的数据
 * @param list 数据列表
 * @param dates 日期列表
 * @param seriesName 系列名称
 * @param dateIndex 日期索引
 * @returns 数据值
 */
const getValueAtDateIndex = (
  list: ILineChartData[],
  dates: string[],
  seriesName: string,
  dateIndex: number,
): number | undefined => {
  if (dateIndex < 0 || dateIndex >= dates.length) return undefined;
  const item = list.find((i) => i.date === dates[dateIndex] && i.name === seriesName);
  return item?.value;
};

/**
 * 检查数据点是否为孤立点
 * @param list 数据列表
 * @param dates 日期列表
 * @param seriesName 系列名称
 * @param dateIndex 当前日期索引
 * @returns 是否为孤立点
 */
const isIsolatedPoint = (list: ILineChartData[], dates: string[], seriesName: string, dateIndex: number): boolean => {
  const currentValue = getValueAtDateIndex(list, dates, seriesName, dateIndex);
  if (!isValidValue(currentValue!)) return false;

  const prevValue = getValueAtDateIndex(list, dates, seriesName, dateIndex - 1);
  const nextValue = getValueAtDateIndex(list, dates, seriesName, dateIndex + 1);

  return !isValidValue(prevValue!) && !isValidValue(nextValue!);
};

const getLineChartDatesAndSeries = (
  list?: ILineChartData[] | null,
  configs?: ILineChartGetterHelpers,
): { dates: string[]; series: ILineSeriesOption[] } => {
  if (!Array.isArray(list) || list.length === 0) return { dates: [], series: [] };
  const getSortedDates = configs?.getSortedDates ?? getSortedDatesDefault;
  const getSortedSeries = configs?.getSortedSeries;
  const dates = new Set<string>();
  const series: ILineSeriesOption[] = [];
  const serieMap: IRecord<true> = {};

  for (const item of list) {
    const date = item?.date || '';
    const name = item?.name || '';
    if (!!date) {
      dates.add(date);
    }
    if (!serieMap[name]) {
      series.push({ type: 'line', name, data: [], showSymbol: false });
      serieMap[name] = true;
    }
  }

  const sortedDates = getSortedDates(Array.from(dates));
  const isSingleDate = sortedDates.length === 1;

  // 填充数据并处理孤立点
  for (const serie of series) {
    for (let dateIndex = 0; dateIndex < sortedDates.length; dateIndex++) {
      const date = sortedDates[dateIndex];
      const item = list?.find((item) => {
        return date === item?.date && serie!.name === item?.name;
      });
      const value = item?.value ?? Number.NaN;
      const payload = item?.payload || {};

      // 检查是否为孤立点
      const isIsolated = isIsolatedPoint(list || [], sortedDates, serie.name as string, dateIndex);

      const dataItem: any = { value, payload };

      // 多日期时，孤立点需要显示符号
      if (!isSingleDate && isIsolated && isValidValue(value)) {
        dataItem.symbol = 'circle';
        dataItem.symbolSize = 6;
      }

      serie!.data!.push(dataItem);
    }

    // 单日期时显示所有数据点
    if (isSingleDate) {
      serie.showSymbol = true;
      serie.symbolSize = 6;
    } else {
      // 多日期时，默认不显示，hover时显示
      serie.showSymbol = false;
      serie.showSymbolOnHover = true;
    }
  }

  return { dates: sortedDates, series: getSortedSeries?.(series) ?? series };
};

/**
 * 默认显示的系列数量
 */
const DEFAULT_VISIBLE_SERIES_COUNT = 10;

/**
 * 获取echarts折线图动态配置
 * @param list 折线图数据
 * @param configs 其他配置 具体配置参见 IChartFormatters定义、ILineChartGetterHelpers定义
 */
export const getLineChartDynamicOption = (
  list?: ILineChartData[] | null,
  configs?: ILineChartHelpers & ILineChartFormatters & ILineChartGetterHelpers,
) => {
  const useXAxisLabelRotate = configs?.useXAxisLabelRotate ?? false;
  const enableXAxisHighLight = configs?.enableXAxisHighLight ?? true;
  const legendAlign = configs?.legendAlign ?? 'center';
  const gridLineStyle = configs?.gridLineStyle;
  const tooltipFormatter = configs?.tooltipFormatter;
  const yAxisLabelFormatter = configs?.yAxisLabelFormatter ?? formatKMBWithPlaceholder;
  const xAxisLabelFormatter = configs?.xAxisLabelFormatter;
  const legendFormatter = configs?.legendFormatter;

  const { dates, series } = getLineChartDatesAndSeries(list, configs);

  // 默认前10个系列显示，其余隐藏
  const selected = series.reduce<Record<string, boolean>>((acc, serie, index) => {
    acc[serie.name as string] = index < DEFAULT_VISIBLE_SERIES_COUNT;
    return acc;
  }, {});

  // 构建 yAxis 配置
  const yAxisConfig: any = {
    axisLabel: {
      formatter: yAxisLabelFormatter,
    },
  };

  // 如果设置了网格线样式
  if (gridLineStyle) {
    yAxisConfig.splitLine = {
      lineStyle: {
        color: '#E9E9E9',
        type: gridLineStyle,
      },
    };
  }

  let option: any = {
    tooltip: {
      formatter: tooltipFormatter,
    },
    legend: {
      selected,
      formatter: legendFormatter,
      left: legendAlign,
    },
    xAxis: [
      {
        data: dates,
        axisLabel: {
          formatter: xAxisLabelFormatter,
          rotate: useXAxisLabelRotate ? 45 : 0,
        },
      },
    ],
    yAxis: [yAxisConfig],
    series,
  };

  // 启用x轴高亮效果
  if (enableXAxisHighLight) {
    option = xAxisHighLight(option, {
      // xAxisLabelFormatter 接收字符串参数，需要从 params 中提取 value
      formatter: xAxisLabelFormatter
        ? (params: any) => xAxisLabelFormatter(params?.value)
        : undefined,
    });
  }

  return option;
};
